import { Calculate } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { FC } from "react";
import { useState } from "react";
import useAuth from "../hooks/useAuth";
import useEntitlements from "../hooks/useEntitlements";
import useMedicalEncounterModal from "../hooks/useMedicalEncounterModal";
import useRoles from "../hooks/useRoles";
import Plus from "../icons/Plus";
import { Roles } from "../pages/appointment/helpers/constants";
import AiNoteCreateModal from "../pages/ai-notes/AiNoteCreateModal";
import { useSelector } from "../store";
import { canWriteMedicalInfo } from "../utils/roleUtils";
import { isAiScribeOnlyNav } from "../utils/shouldRenderMenuItem";
import useShortcuts from "./shortcuts/hooks/useShortcuts";

const ActionsMenu: FC = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [aiNoteCreateOpen, setAiNoteCreateOpen] = useState(false);
  const { openMedicalEncounterModal } = useMedicalEncounterModal();
  const { setOpen } = useShortcuts();
  const {
    allowShortcutsMenu,
    allowCopilot,
    allowAiNotes,
    medicalEncountersFeature,
    newMedicalEncounterActionMenu,
    newAppointmentActionMenu,
    newPatientActionMenu,
    newBroadcastActionMenu,
  } = useFlags();
  const userRoles = useRoles();
  const { can, entitlements } = useEntitlements();
  const currentOrg = useSelector((state) => state.user.currentOrganization);

  const isScribeOnly = isAiScribeOnlyNav(
    can,
    currentOrg?.adminSettings,
    entitlements?.tier,
  );
  const canAccessAppointments =
    !!newAppointmentActionMenu && can("feature_appointments") && !isScribeOnly;
  const canAccessEncounters =
    !!newMedicalEncounterActionMenu &&
    !!medicalEncountersFeature &&
    can("feature_clinical_encounters") &&
    !isScribeOnly;
  const canAccessPatients =
    !!newPatientActionMenu && can("feature_patient_mgmt");
  // Broadcast create still requires Administrator (same as BroadcastDashboard).
  const canAccessMessaging =
    !!newBroadcastActionMenu &&
    can("feature_messaging") &&
    !!userRoles?.includes(Roles.ADMINISTRATOR);
  const canCreateAiNote =
    !!allowAiNotes &&
    !!allowCopilot &&
    can("ai_scribe") &&
    canWriteMedicalInfo(userRoles);

  const showNewItemMenu =
    canAccessPatients ||
    canAccessMessaging ||
    canAccessAppointments ||
    canAccessEncounters ||
    canCreateAiNote;

  const open = Boolean(anchorEl);

  const handleOpen = (event): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const openEncounterModal = () => {
    handleClose();
    openMedicalEncounterModal();
  };

  const openAiNoteCreate = () => {
    handleClose();
    setAiNoteCreateOpen(true);
  };

  const { isAuthenticated } = useAuth();

  return isAuthenticated ? (
    <>
      <Box display="flex" flexDirection="row">
        {allowShortcutsMenu && (
          <Tooltip title="Calculators">
            <IconButton color="inherit" onClick={() => setOpen(true)}>
              <Calculate />
            </IconButton>
          </Tooltip>
        )}
        {showNewItemMenu && (
          <Tooltip title="New Item">
            <IconButton color="inherit" onClick={handleOpen}>
              <Plus fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {canAccessPatients && (
          <MenuItem
            onClick={() => {
              handleClose();
              window.open("/patients/new");
            }}
          >
            New Patient
          </MenuItem>
        )}
        {canAccessMessaging && (
          <MenuItem
            onClick={() => {
              handleClose();
              window.open("/communications/broadcast/new");
            }}
          >
            New Message
          </MenuItem>
        )}
        {canAccessAppointments && (
          <MenuItem
            onClick={() => {
              handleClose();
              window.open("/appointments/new");
            }}
          >
            New Appointment
          </MenuItem>
        )}
        {canAccessEncounters && (
          <MenuItem onClick={openEncounterModal}>
            New Medical Encounter
          </MenuItem>
        )}
        {canCreateAiNote && (
          <MenuItem onClick={openAiNoteCreate}>New AI Note</MenuItem>
        )}
      </Menu>

      <AiNoteCreateModal
        open={aiNoteCreateOpen}
        onClose={() => setAiNoteCreateOpen(false)}
      />
    </>
  ) : null;
};

export default ActionsMenu;
